import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { forwardRef, useEffect, useRef, useState } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import ListItemText from '@mui/material/ListItemText';
import { Box } from '@mui/system';
// routes
import { RouterLink } from 'src/routes/components';
//
import { usePlanFeature } from 'src/hooks/usePlanFeature';
import { openUpgradeDialog } from 'src/redux/slices/planSlice';
import PlanBadge from 'src/components/plan-badge/PlanBadge';
import Iconify from '../../iconify';
import { StyledItem, StyledIcon } from './styles';

// ----------------------------------------------------------------------

const NavItem = forwardRef(({ item, depth, open, active, externalLink, config, ...other }, ref) => {
  const drawerWidth = useSelector((state) => state.drawer.drawerWidth);
  const theme = useTheme();
  const { title, path, icon, info, children, disabled, caption, feature } = item;

  const dispatch = useDispatch();
  const { isLocked, requiredPlan, shouldShowBadge, type, value } = usePlanFeature(feature);

  const textRef = useRef(null);
  const measureRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (drawerWidth <= 108 && textRef.current && measureRef.current) {
      const containerWidth = textRef.current.clientWidth;
      const fullTextWidth = measureRef.current.scrollWidth;

      if (fullTextWidth > containerWidth) {
        setIsTruncated(true);
      } else {
        setIsTruncated(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerWidth]);

  const subItem = depth !== 1;

  const handleClick = (e) => {
    if (isLocked) {
      e.preventDefault();
      e.stopPropagation();
      dispatch(
        openUpgradeDialog({
          requiredPlan,
          featureLabel: title,
          featureType: type,
          limitValue: value,
        })
      );
    } else if (other.onClick) {
      other.onClick(e);
    }
  };

  // Render content for the navigation item
  const renderContent = (
    <>
      {/* Hidden element to measure the actual text width */}
      <Box
        ref={measureRef}
        sx={{
          position: 'absolute',
          top: -9999,
          left: -9999,
          whiteSpace: 'nowrap',
          visibility: 'hidden',
        }}
      >
        {title}
      </Box>

      <StyledItem
        disableGutters
        ref={ref}
        open={open}
        depth={depth}
        active={active}
        disabled={disabled}
        config={config}
        className={active ? 'active' : ''}
        {...other}
        onClick={handleClick}
      >
        {icon && (
          <Tooltip title={isTruncated ? title : null} arrow placement="top">
            <StyledIcon
              size={config.iconSize}
              sx={{
                ...(subItem && { mr: 1.5 }),
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              }}
            >
              {icon}
            </StyledIcon>
          </Tooltip>
        )}

        {!(config.hiddenLabel && !subItem) && (
          <ListItemText
            ref={textRef} // Attach the ref
            sx={{
              width: 1,
              flex: 'unset',
              ...(!subItem && {
                px: 0.5,
                mt: 0.5,
              }),
              fontSize: subItem ? theme.typography.body2.fontSize : 10,
              lineHeight: subItem ? theme.typography.body2.lineHeight : '16px',
              textAlign: subItem ? 'unset' : 'center',
              fontWeight: active ? 'fontWeightBold' : 'fontWeightSemiBold',
              transition: 'font-weight 0.3s ease',
            }}
            primary={
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  justifyContent: subItem ? 'flex-start' : 'center',
                  width: 1,
                }}
              >
                {isTruncated ? (
                  <Tooltip title={title} arrow placement="top">
                    <span>{title}</span>
                  </Tooltip>
                ) : (
                  <span>{title}</span>
                )}
                {shouldShowBadge && (
                  <PlanBadge
                    plan={requiredPlan}
                    isLocked={isLocked}
                    variant="mini"
                  />
                )}
              </Box>
            }
            primaryTypographyProps={{
              noWrap: true, // Keep noWrap but detect truncation correctly
              textTransform: 'capitalize',
            }}
          />
        )}

        {caption && (
          <Tooltip title={caption} arrow placement="right">
            <Iconify
              width={16}
              icon="eva:info-outline"
              sx={{
                color: 'text.disabled',
                transition: 'color 0.3s ease',
                '&:hover': {
                  color: 'text.primary',
                },
                ...(!subItem && {
                  top: 11,
                  left: 6,
                  position: 'absolute',
                }),
              }}
            />
          </Tooltip>
        )}

        {item.title !== 'Approvals' && info && (
          <>
            <Box component="span" sx={{ ml: 1, lineHeight: 0 }}>
              {info}
            </Box>
            {(!!children || !children) && <Box width={16} sx={{ ml: 1, flexShrink: 0 }} />}
          </>
        )}
        {item.title === 'Approvals' && info && (
          <>
            <Box component="span" sx={{ top: 10, left: 6, position: 'absolute' }}>
              {info}
            </Box>
          </>
        )}

        {!!children && (
          <Iconify
            width={16}
            icon="eva:arrow-ios-forward-fill"
            sx={{
              top: 11,
              right: 6,
              position: 'absolute',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'translateX(5px)',
              },
            }}
          />
        )}
      </StyledItem>
    </>
  );

  // External link
  if (externalLink) {
    return (
      <Link
        href={path}
        target="_blank"
        rel="noopener"
        underline="none"
        onClick={handleClick}
        sx={{
          width: 1,
          ...(disabled && {
            cursor: 'default',
          }),
        }}
      >
        {renderContent}
      </Link>
    );
  }

  // Default (Internal Link)
  return (
    <Link
      component={RouterLink}
      href={path}
      underline="none"
      onClick={handleClick}
      sx={{
        width: 1,
        ...(disabled && {
          cursor: 'default',
        }),
      }}
    >
      {renderContent}
    </Link>
  );
});

NavItem.propTypes = {
  active: PropTypes.bool,
  config: PropTypes.object,
  depth: PropTypes.number,
  externalLink: PropTypes.bool,
  item: PropTypes.object,
  open: PropTypes.bool,
};

export default NavItem;
